import React from "react";
import {
   Box,
   Button,
   Card,
   Flex,
   FlexList,
   Spinner,
   useI18nContext,
   EmptyState,
   Title,
   H2,
} from "@procore/core-react";
import type { DetailsAssignmentCardProps, ListAssignmentProps } from "./types";
import type { ColumnDefinition, DateCellColumnDefinition } from "@procore/data-table";
import { ClientSideDataTable, DateCellEditor, DateCellRenderer } from "@procore/data-table";
import {
   getAssignmentTableHeight,
   PERSON_ASSIGNMENTS_PAGE_SIZE,
} from "./person-assignments-table-utils";
import { DateFilter } from "../../../data-table/custom-filters/date-filter";
import { ColorSelectRenderer } from "@/react/components/data-table/ColorSelectComponent/ColorSelectColumn";
import { CustomLinkCellRenderer } from "../../../data-table/custom-link-column/custom-link-cell";
import { usePersonTearsheet } from "../people-tearsheet";
import { TearsheetTabs, PageTitle } from "@/react/prop-types";
import classnames from "classnames";
import styles from "../../project/project-detail-cards/project-details.scss";
import { useGetCurrentAssignments, useGetNextAssignments } from "../../assignment/queries";
import { getCurrentNextAssignmentsFormatted } from "../../assignment/helpers";
import type { FormattedAssignmentData } from "../../assignment/types";
import { PastAssignmentsTable } from "./past-assignments-table";
import type { ProjectTearsheetActions } from "../../project/project-tearsheet";

interface EmptyAssignmentsProps {
   openAddAssignmentModal: () => void;
   canManageAssignment: boolean;
   showButton?: boolean;
   title?: string;
   emptyStateTitle?: string;
   emptyStateDescription?: string;
}
const cx = classnames.bind(styles);

interface AssignmentTableProps {
   formattedAssignmentData: FormattedAssignmentData[] | undefined;
   isLoading: boolean;
   title: string;
   showIndividualEmptyState?: boolean;
   onAddAssignment?: () => void;
   canManageAssignment?: boolean;
   emptyStateTitle?: string;
   emptyStateDescription?: string;
   projectTearsheetDispatch?: (action: ProjectTearsheetActions) => void;
}

const AssignmentTable: React.FC<AssignmentTableProps> = ({
   formattedAssignmentData,
   isLoading,
   title,
   showIndividualEmptyState = false,
   onAddAssignment,
   canManageAssignment = false,
   emptyStateTitle,
   emptyStateDescription,
   projectTearsheetDispatch,
}) => {
   const I18n = useI18nContext();

   // If no data and we should show individual empty state
   if (!isLoading && (!formattedAssignmentData || formattedAssignmentData.length === 0)) {
      if (showIndividualEmptyState) {
         return (
            <Card style={{ marginBottom: "16px" }}>
               <Box padding="sm">
                  <EmptyAssignments
                     openAddAssignmentModal={onAddAssignment || (() => {})}
                     canManageAssignment={canManageAssignment}
                     showButton={false}
                     title={title}
                     emptyStateTitle={emptyStateTitle}
                     emptyStateDescription={emptyStateDescription}
                  />
               </Box>
            </Card>
         );
      }
      return null;
   }

   const projectNameColumn: ColumnDefinition = {
      field: "project_name",
      headerName: I18n.t("views.company.workforce_planning.project"),
      ...(projectTearsheetDispatch && {
         cellRenderer: CustomLinkCellRenderer,
         cellRendererParams: {
            pageTitle: PageTitle.PEOPLE_LIST,
            getProjectDetailDispatch: projectTearsheetDispatch,
         },
      }),
      getStringFormattedValue: (value) => value,
      editable: false,
      flex: 15,
      minWidth: 350,
   };
   const jobTitleColumn: ColumnDefinition = {
      field: "job_title",
      cellRenderer: ColorSelectRenderer,
      cellRendererParams: {
         getColor: (item: { color: string }) => item.color,
         getShape: (item: { shape: string }) => item.shape,
      },
      getStringFormattedValue: (item) => item.label,
      headerName: I18n.t("views.company.workforce_planning.people.job_title"),
      editable: false,
      minWidth: 150,
   };
   const startDateColumn: DateCellColumnDefinition = {
      field: "start_date",
      headerName: I18n.t("views.company.workforce_planning.start_date"),
      cellRenderer: DateCellRenderer,
      cellRendererParams: {
         timeZone: "UTC",
      },
      cellEditor: DateCellEditor,
      filterRenderer: DateFilter as any,
      flex: 8,
      minWidth: 100,
   };
   const endDateColumn: DateCellColumnDefinition = {
      field: "end_date",
      headerName: I18n.t("views.company.workforce_planning.end_date"),
      cellRenderer: DateCellRenderer,
      cellRendererParams: {
         timeZone: "UTC",
      },
      cellEditor: DateCellEditor,
      filterRenderer: DateFilter as any,
      flex: 8,
      minWidth: 100,
   };

   const assignmentColumnDefinitions: ColumnDefinition[] = [
      projectNameColumn,
      jobTitleColumn,
      startDateColumn,
      endDateColumn,
   ];

   const rowCount = formattedAssignmentData?.length ?? 0;
   const { tableHeight, showPagination } = getAssignmentTableHeight(rowCount);

   return (
      <Card style={{ marginBottom: "16px", overflow: "hidden" }}>
         <Box padding="sm">
            <FlexList
               size="xs"
               alignItems="center"
               justifyContent="space-between"
               paddingBottom="md"
            >
               <Box>
                  <Title>
                     <Title.Text>
                        <H2>{title}</H2>
                     </Title.Text>
                  </Title>
               </Box>
            </FlexList>

            <ClientSideDataTable columnDefinitions={assignmentColumnDefinitions}>
               <Flex
                  style={{
                     width: "100%",
                  }}
                  alignItems="stretch"
               >
                  <Box
                     flex="1"
                     display="flex-column"
                     alignItems="stretch"
                     style={{
                        height: `${tableHeight}px`,
                        width: "100%",
                     }}
                  >
                     <ClientSideDataTable.Table
                        rows={formattedAssignmentData || []}
                        pagination={showPagination}
                        paginationPageSize={PERSON_ASSIGNMENTS_PAGE_SIZE}
                        suppressColumnVirtualisation={false}
                        rowActionsConfig={{
                           sortable: false,
                        }}
                     />
                  </Box>
                  <Flex flex="0 1 0px" alignItems="stretch">
                     <ClientSideDataTable.ContextPanel style={{ marginLeft: "16px" }} />
                  </Flex>
               </Flex>
            </ClientSideDataTable>
         </Box>
      </Card>
   );
};

const EmptyAssignments: React.FC<EmptyAssignmentsProps> = ({
   openAddAssignmentModal,
   canManageAssignment,
   showButton = true,
   title,
   emptyStateTitle,
   emptyStateDescription,
}) => {
   const I18n = useI18nContext();

   const defaultEmptyAssignmentsTitle =
      "views.company.workforce_planning.people.assignments_tab.empty_state_title";

   const defaultEmptyAssignmentsDescription =
      "views.company.workforce_planning.people.assignments_tab.empty_state_description";

   const buttonText =
      "views.company.workforce_planning.people.assignments_tab.add_assignment_button";

   const noAssignmentsText =
      "views.company.workforce_planning.people.assignments_tab.no_assignments";

   return (
      <Box padding="sm">
         {title && (
            <Title>
               <Title.Text>
                  <H2>{title}</H2>
               </Title.Text>
            </Title>
         )}
         <EmptyState compact>
            <EmptyState.NoItems />
            <EmptyState.Title className={cx("empty-state-title")}>
               {I18n.t(
                  emptyStateTitle ||
                     (showButton ? defaultEmptyAssignmentsTitle : noAssignmentsText),
               )}
            </EmptyState.Title>
            <EmptyState.Description className={cx("empty-state-description")}>
               {I18n.t(emptyStateDescription || defaultEmptyAssignmentsDescription)}
            </EmptyState.Description>

            {showButton && canManageAssignment && (
               <EmptyState.Actions className={cx("empty-state-actions")}>
                  <Button variant="secondary" onClick={openAddAssignmentModal}>
                     {I18n.t(buttonText)}
                  </Button>
               </EmptyState.Actions>
            )}
         </EmptyState>
      </Box>
   );
};

const ListAssignmentCard: React.FC<ListAssignmentProps> = ({
   canManageAssignment,
   onAddAssignment,
   currentAssignments,
   isLoading,
   nextAssignments,
   isNextAssignmentLoading,
   personId,
   projectTearsheetDispatch,
   refetchData,
}) => {
   const I18n = useI18nContext();

   const formattedCurrentAssignmentData = React.useMemo(() => {
      if (!currentAssignments?.data) return [];
      return getCurrentNextAssignmentsFormatted(currentAssignments?.data);
   }, [currentAssignments]);

   const formattedNextAssignmentData = React.useMemo(() => {
      if (!nextAssignments?.data) return [];
      return getCurrentNextAssignmentsFormatted(nextAssignments?.data);
   }, [nextAssignments]);

   const handleAddAssignment = () => {
      if (onAddAssignment) {
         onAddAssignment();
      }
   };

   // Show empty state only when not loading and no assignments at all
   const hasNoAssignments =
      !isLoading &&
      !isNextAssignmentLoading &&
      (!formattedCurrentAssignmentData || formattedCurrentAssignmentData.length === 0) &&
      (!formattedNextAssignmentData || formattedNextAssignmentData.length === 0);

   if (hasNoAssignments) {
      return (
         <EmptyAssignments
            openAddAssignmentModal={handleAddAssignment}
            canManageAssignment={canManageAssignment}
         />
      );
   }

   return (
      <>
         <AssignmentTable
            formattedAssignmentData={formattedCurrentAssignmentData}
            isLoading={isLoading}
            title={I18n.t(
               "views.company.workforce_planning.people.assignments_tab.current_assignments",
            )}
            showIndividualEmptyState={true}
            onAddAssignment={handleAddAssignment}
            canManageAssignment={canManageAssignment}
            emptyStateTitle="views.company.workforce_planning.people.assignments_tab.add_current_assignment_title"
            emptyStateDescription="views.company.workforce_planning.people.assignments_tab.empty_state_title"
            projectTearsheetDispatch={projectTearsheetDispatch}
         />
         <AssignmentTable
            formattedAssignmentData={formattedNextAssignmentData}
            isLoading={isNextAssignmentLoading}
            title={I18n.t(
               "views.company.workforce_planning.people.assignments_tab.next_assignments",
            )}
            showIndividualEmptyState={true}
            onAddAssignment={handleAddAssignment}
            canManageAssignment={canManageAssignment}
            emptyStateTitle="views.company.workforce_planning.people.assignments_tab.add_next_assignment_title"
            emptyStateDescription="views.company.workforce_planning.people.assignments_tab.empty_state_title"
            projectTearsheetDispatch={projectTearsheetDispatch}
         />
         {personId && (
            <PastAssignmentsTable
               key={personId}
               personId={personId}
               projectTearsheetDispatch={projectTearsheetDispatch}
               refetchAssignments={refetchData}
            />
         )}
      </>
   );
};

export const DetailsAssignmentsCard: React.FC<DetailsAssignmentCardProps> = ({
   loading: DataLoading,
   personId,
   people,
   canManageAssignment,
   refetchData,
   projectTearsheetDispatch,
}) => {
   const { dispatch: personTearsheetDispatch } = usePersonTearsheet();
   const { currentAssignments, isLoading } = useGetCurrentAssignments(
      personId || "",
      PERSON_ASSIGNMENTS_PAGE_SIZE,
      "person-detail-assignments",
   );
   const { nextAssignments, isNextAssignmentLoading } = useGetNextAssignments(personId || "");
   const I18n = useI18nContext();

   const handleAddAssignment = () => {
      personTearsheetDispatch({
         type: "open-create-assignment",
         people,
         callback: () => {
            if (refetchData) {
               refetchData();
            }
            // Reopen person detail tearsheet with Assignments tab selected
            personTearsheetDispatch({
               type: "open-person-detail",
               personId,
               showActiveTab: TearsheetTabs.ASSIGNMENTS,
            });
         },
      });
   };

   const hasAssignments =
      (currentAssignments?.data && currentAssignments.data.length > 0) ||
      (nextAssignments?.data && nextAssignments.data.length > 0);

   return (
      <Spinner loading={isLoading || isNextAssignmentLoading || DataLoading}>
         <Card>
            <Box padding="md">
               {hasAssignments && canManageAssignment && (
                  <Box display="flex" justifyContent="flex-end" marginBottom="md">
                     <Button variant="secondary" onClick={handleAddAssignment}>
                        {I18n.t(
                           "views.company.workforce_planning.people.assignments_tab.add_assignment_button",
                        )}
                     </Button>
                  </Box>
               )}

               <ListAssignmentCard
                  refetchData={refetchData}
                  personId={personId}
                  canManageAssignment={canManageAssignment}
                  onAddAssignment={handleAddAssignment}
                  currentAssignments={currentAssignments}
                  isLoading={isLoading}
                  nextAssignments={nextAssignments}
                  isNextAssignmentLoading={isNextAssignmentLoading}
                  projectTearsheetDispatch={projectTearsheetDispatch}
               />
            </Box>
         </Card>
      </Spinner>
   );
};
